import { useEffect, forwardRef } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import {
  createCurrentSeries,
  createPotentialSeries,
  createTotalSeries,
  configureLegend,
  configureAxes,
  processMultiValueStacketBarChartData,
} from "./utils";
import { MultiValueStackedBarChartData } from "./types";
import { TAB_VIEW } from "../../../pages/Workspace/WorkspaceConfiguration/constants";
import { applyAppFontFamily } from "../utils";

interface MultiValueStackedBarChartProps {
  id: string;
  chartData: MultiValueStackedBarChartData[];
  onInsufficientButtonClick: (segment: TAB_VIEW) => void;
  onAdditionalInfoLinkClick: () => void;
  legendItems?: Array<{ name: string; color: string }>;
  isHierarchyMatchingEnabled?: boolean;
  legendInteractionDisabled?: boolean;
  legendPosition?: "top" | "bottom" | "left" | "right";
  legendAlign?: "left" | "center" | "right";
  showOnlyCurrentCompanies?: boolean;
}

export const MultiValueStackedBarChart = forwardRef<
  am4charts.XYChart,
  MultiValueStackedBarChartProps
>((props, ref) => {
  useEffect(() => {
    const chart = am4core.create(props.id, am4charts.XYChart);

    chart.numberFormatter.numberFormat = "#.#a";
    chart.numberFormatter.bigNumberPrefixes = [
      { number: 1e3, suffix: "K" },
      { number: 1e6, suffix: "M" },
      { number: 1e9, suffix: "B" },
      { number: 1e12, suffix: "T" },
    ];

    chart.data = props.chartData;

    chart.paddingTop = props.legendItems ? -9 : -18;
    chart.paddingRight = 0;
    chart.paddingBottom = 0;
    chart.paddingLeft = -11;

    const processedData = processMultiValueStacketBarChartData(
      props.chartData,
      props.isHierarchyMatchingEnabled
    );

    chart.data = processedData;

    configureAxes(chart);
    createCurrentSeries(chart, props.onAdditionalInfoLinkClick);
    if (!props.showOnlyCurrentCompanies) {
      createPotentialSeries(chart, props.isHierarchyMatchingEnabled);
    }
    createTotalSeries(chart, props.onInsufficientButtonClick);
    configureLegend(chart, props.legendItems, {
      interactionDisabled: props.legendInteractionDisabled,
      position: props.legendPosition,
      align: props.legendAlign,
    });
    applyAppFontFamily(chart);

    if (typeof ref === "function") {
      ref(chart);
    } else if (ref) {
      ref.current = chart;
    }

    return () => {
      chart.dispose();
    };
  }, [
    props.chartData,
    props.id,
    props.onInsufficientButtonClick,
    props.onAdditionalInfoLinkClick,
    props.legendItems,
    props.isHierarchyMatchingEnabled,
    props.legendInteractionDisabled,
    props.legendPosition,
    props.legendAlign,
    props.showOnlyCurrentCompanies,
    ref,
  ]);

  return <div id={props.id} style={{ width: "100%", height: "100%" }} />;
});
